/**
 * VideoTextureBridge -- Imports VideoFrameDecoder ring textures into the C++
 * renderer via emdawnwebgpu and orchestrates frame sync (video frame N = depth frame N).
 *
 * Lifecycle:
 * 1. Constructor receives decoder + renderer + module references
 * 2. importRingTextures() imports all 3 ring GPUTextures upfront
 * 3. setIntrinsics() sends camera intrinsics to C++ for quad corner computation
 * 4. decodeAndSync(frameIndex, c2wMatrix) decodes a frame and updates both the
 *    active ring index and quad pose in C++
 * 5. destroy() clears C++ texture handles before decoder cleanup (Pitfall 7)
 */

import { VideoFrameDecoder } from './VideoFrameDecoder';

// ─── Camera data types ───────────────────────────────────────────────────────

export interface CameraFrame {
  /** 16 floats, row-major 4x4 world-to-camera */
  w2c: number[];
  /** 16 floats, row-major 4x4 camera-to-world (inverse of w2c) */
  c2w: number[];
  intrinsics: { fx: number; fy: number; cx: number; cy: number };
  width: number;
  height: number;
}

// ─── Matrix utilities ────────────────────────────────────────────────────────

/**
 * Flatten a nested 4x4 matrix ([[row0], [row1], ...]) to a flat 16-element
 * row-major array. If already flat (length 16), returns a copy.
 */
export function flattenMatrix(m: number[][] | number[]): number[] {
  if (!Array.isArray(m)) return [];
  // Already flat
  if (typeof m[0] === 'number') {
    return (m as number[]).slice();
  }
  // Nested rows
  const flat: number[] = [];
  for (const row of m as number[][]) {
    for (const v of row) {
      flat.push(v);
    }
  }
  return flat;
}

/**
 * Invert a 4x4 matrix given as a 16-element row-major array.
 * Returns a new 16-element row-major array, or null if singular.
 *
 * Uses the standard cofactor expansion approach.
 */
export function invertMatrix4x4(m: number[]): number[] | null {
  // Indices into the flat row-major array:
  // [0  1  2  3 ]
  // [4  5  6  7 ]
  // [8  9  10 11]
  // [12 13 14 15]

  const m00 = m[0],  m01 = m[1],  m02 = m[2],  m03 = m[3];
  const m10 = m[4],  m11 = m[5],  m12 = m[6],  m13 = m[7];
  const m20 = m[8],  m21 = m[9],  m22 = m[10], m23 = m[11];
  const m30 = m[12], m31 = m[13], m32 = m[14], m33 = m[15];

  const b00 = m00 * m11 - m01 * m10;
  const b01 = m00 * m12 - m02 * m10;
  const b02 = m00 * m13 - m03 * m10;
  const b03 = m01 * m12 - m02 * m11;
  const b04 = m01 * m13 - m03 * m11;
  const b05 = m02 * m13 - m03 * m12;
  const b06 = m20 * m31 - m21 * m30;
  const b07 = m20 * m32 - m22 * m30;
  const b08 = m20 * m33 - m23 * m30;
  const b09 = m21 * m32 - m22 * m31;
  const b10 = m21 * m33 - m23 * m31;
  const b11 = m22 * m33 - m23 * m32;

  const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;

  if (Math.abs(det) < 1e-12) {
    return null; // Singular matrix
  }

  const invDet = 1.0 / det;

  return [
    ( m11 * b11 - m12 * b10 + m13 * b09) * invDet,
    (-m01 * b11 + m02 * b10 - m03 * b09) * invDet,
    ( m31 * b05 - m32 * b04 + m33 * b03) * invDet,
    (-m21 * b05 + m22 * b04 - m23 * b03) * invDet,
    (-m10 * b11 + m12 * b08 - m13 * b07) * invDet,
    ( m00 * b11 - m02 * b08 + m03 * b07) * invDet,
    (-m30 * b05 + m32 * b02 - m33 * b01) * invDet,
    ( m20 * b05 - m22 * b02 + m23 * b01) * invDet,
    ( m10 * b10 - m11 * b08 + m13 * b06) * invDet,
    (-m00 * b10 + m01 * b08 - m03 * b06) * invDet,
    ( m30 * b04 - m31 * b02 + m33 * b00) * invDet,
    (-m20 * b04 + m21 * b02 - m23 * b00) * invDet,
    (-m10 * b09 + m11 * b07 - m12 * b06) * invDet,
    ( m00 * b09 - m01 * b07 + m02 * b06) * invDet,
    (-m30 * b03 + m31 * b01 - m32 * b00) * invDet,
    ( m20 * b03 - m21 * b01 + m22 * b00) * invDet,
  ];
}

// ─── Camera data fetch ───────────────────────────────────────────────────────

/**
 * Fetch and parse camera data from the test set API.
 * Returns per-frame camera parameters with both w2c and c2w matrices.
 */
export async function fetchCameraData(url: string): Promise<CameraFrame[]> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch camera data: ${response.status} ${response.statusText}`);
  }
  const json = await response.json();

  // The camera_data JSON from the test set API has a "frames" array
  const frames = json.frames || json;
  if (!Array.isArray(frames)) {
    throw new Error('Camera data must contain a "frames" array');
  }

  return frames.map((frame: any, i: number) => {
    // Support both "w2c" (legacy) and "extrinsics" (current API format) fields
    const rawMatrix = frame.w2c || frame.extrinsics;
    let w2c: number[];
    if (!rawMatrix) {
      console.warn(`[fetchCameraData] Frame ${i}: no w2c or extrinsics field`);
      w2c = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    } else {
      w2c = flattenMatrix(rawMatrix);
      // If 3x4 matrix (12 elements), pad to 4x4 by adding [0, 0, 0, 1]
      if (w2c.length === 12) {
        w2c = [...w2c, 0, 0, 0, 1];
      }
    }

    const c2w = invertMatrix4x4(w2c);
    if (!c2w) {
      console.warn(`[fetchCameraData] Frame ${i}: singular w2c matrix, using identity`);
    }
    const identity = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

    // Parse intrinsics — support 3x3 K matrix, object {fx,fy,cx,cy}, or flat fields
    let intrinsics: { fx: number; fy: number; cx: number; cy: number };
    const K = frame.intrinsics;
    if (Array.isArray(K) && Array.isArray(K[0])) {
      // 3x3 K matrix: [[fx, 0, cx], [0, fy, cy], [0, 0, 1]]
      intrinsics = { fx: K[0][0], fy: K[1][1], cx: K[0][2], cy: K[1][2] };
    } else if (K && typeof K.fx === 'number') {
      intrinsics = { fx: K.fx, fy: K.fy, cx: K.cx, cy: K.cy };
    } else {
      intrinsics = { fx: frame.fx, fy: frame.fy, cx: frame.cx, cy: frame.cy };
    }

    return {
      w2c,
      c2w: c2w || identity,
      intrinsics,
      width: frame.width,
      height: frame.height,
    };
  });
}

// ─── VideoTextureBridge ──────────────────────────────────────────────────────

export class VideoTextureBridge {
  private decoder: VideoFrameDecoder;
  private renderer: any;  // EMBIND renderer (window._scRenderer)
  private module: any;    // WASM module with .WebGPU property
  private imported: boolean = false;

  /**
   * @param decoder A VideoFrameDecoder that has completed load()
   * @param renderer The EMBIND renderer instance (window._scRenderer)
   * @param module The WASM module instance (from getWasmModule())
   */
  constructor(decoder: VideoFrameDecoder, renderer: any, module: any) {
    this.decoder = decoder;
    this.renderer = renderer;
    this.module = module;
  }

  /**
   * Import all ring buffer GPUTextures into the C++ renderer upfront.
   * Each texture gets a persistent emdawnwebgpu handle that the renderer
   * uses to create per-slot bind groups (zero per-frame allocation).
   */
  importRingTextures(): void {
    if (!this.module?.WebGPU?.importJsTexture) {
      throw new Error('[VideoTextureBridge] Module.WebGPU.importJsTexture not available');
    }

    const ringTextures = this.decoder.getRingTextures();
    if (ringTextures.length === 0) {
      throw new Error('[VideoTextureBridge] No ring textures available. Has decoder.load() completed?');
    }

    for (let i = 0; i < ringTextures.length; i++) {
      const handle = this.module.WebGPU.importJsTexture(ringTextures[i]);
      this.renderer.setVideoTexture(i, handle, this.decoder.videoWidth, this.decoder.videoHeight);
    }

    this.imported = true;
    console.log(
      `[VideoTextureBridge] Imported ${ringTextures.length} ring textures ` +
      `(${this.decoder.videoWidth}x${this.decoder.videoHeight})`
    );
  }

  /**
   * Send camera intrinsics to C++ for quad corner computation.
   */
  setIntrinsics(fx: number, fy: number, cx: number, cy: number): void {
    this.renderer.setVideoIntrinsics(fx, fy, cx, cy);
  }

  /**
   * Decode a video frame, update the C++ ring index, and set the quad pose
   * using C++'s coordinate-converted frustum data.
   *
   * @param frameIndex The video/depth frame index to decode
   * @param _c2wMatrix Unused (kept for API compat) — C++ computes pose from loaded pipeline data
   */
  async decodeAndSync(frameIndex: number, _c2wMatrix?: number[]): Promise<void> {
    // Decode the frame (uploads to ring buffer)
    await this.decoder.decodeFrame(frameIndex);

    // Get the ring slot that was just written to
    const ringIndex = this.decoder.getLastDecodedRingIndex();

    // Tell C++ which ring slot is active
    this.renderer.setVideoRingIndex(ringIndex);

    // Set pose + intrinsics from C++ pipeline data (proper ECEF coordinate conversion)
    this.renderer.setVideoQuadFrame(frameIndex);
  }

  /**
   * Clear C++ texture handles. MUST be called before decoder.destroy()
   * to avoid dangling GPU texture references in the C++ bind groups.
   */
  destroy(): void {
    if (this.imported) {
      try {
        this.renderer.clearVideoTextures();
      } catch (err) {
        console.warn('[VideoTextureBridge] Error clearing video textures:', err);
      }
      this.imported = false;
    }
  }
}
